import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../EditDiv/EditDiv.module.css';
import buttonStyles from '../YesNoButtonsCSS/YesNoButtons.module.css';
import BlueLogo from '../../assets/BlueLogo.png';
import { Grid } from '@mui/material';
import { useSnackbar } from 'notistack';
import TickBox from '../TickBox/TickBox';
import Textfeild from '../Textfeild/Textfeild';
import { useDispatch, useSelector } from '../../redux/store';
import { submitRooAndDownloadDocx } from '../../redux/slices/roo';

function RisksOptionsForm({ onClose, onSubmit, initialJobNumber }) {
  const { enqueueSnackbar } = useSnackbar();
  const backdropRef = useRef(null);
  const formRef = useRef(null);

  const risksList = useMemo(() => ([
    'Site unknowns',
    'Trees nearby',
    'Existing foundations unknown',
    'Drainage unknown',
    'Existing Steelwork',
    'Ground conditions unknown',
    'Fire damaged brickwork',
    'Reuse existing foundations',
    'None of the above',
  ]), []);

  const optionsList = useMemo(() => ([
    'Use lighter UB sections',
    'Use shallower UC sections',
    'Ground floor construction',
    'RHS columns for frame',
    'Beam XYZ to be pre-loaded',
    'None of the above',
  ]), []);

  const opportunitiesList = useMemo(() => ([
    'Allow for future Loft conversion',
    'Intermediate column',
    'None of the above',
  ]), []);

  const [jobNumber, setJobNumber] = useState(initialJobNumber || '');
  const [revision, setRevision] = useState('');
  const [risks, setRisks] = useState(() => Object.fromEntries(risksList.map(l => [l, false])));
  const [options, setOptions] = useState(() => Object.fromEntries(optionsList.map(l => [l, false])));
  const [opportunities, setOpportunities] = useState(() => Object.fromEntries(opportunitiesList.map(l => [l, false])));
  const dispatch = useDispatch();
  const rooState = useSelector((s) => s.roo || {});

  // Disabled items that should be greyed out and not selectable
  const disabledOptions = useMemo(() => new Set([]), []);
  const disabledOpportunities = useMemo(() => new Set([]), []);

  // Ensure modal opens scrolled to the top and content is top-aligned
  useEffect(() => {
    if (backdropRef.current) backdropRef.current.scrollTop = 0;
    if (formRef.current) formRef.current.scrollTop = 0;
  }, []);

  const toggleItem = (groupName, label) => {
    // Guard: prevent toggling disabled items
    if (
      (groupName === 'options' && disabledOptions.has(label)) ||
      (groupName === 'opportunities' && disabledOpportunities.has(label))
    ) {
      return;
    }
    const updateGroup = (group, setGroup, list) => {
      const newState = { ...group };
      if (label === 'None of the above') {
        // Selecting "None of the above" clears the rest
        list.forEach(l => { newState[l] = (l === 'None of the above') ? !group['None of the above'] : false; });
      } else {
        newState[label] = !group[label];
        // If any other item is selected, clear "None of the above"
        if (newState[label]) newState['None of the above'] = false;
      }
      setGroup(newState);
    };

    if (groupName === 'risks') updateGroup(risks, setRisks, risksList);
    if (groupName === 'options') updateGroup(options, setOptions, optionsList);
    if (groupName === 'opportunities') updateGroup(opportunities, setOpportunities, opportunitiesList);
  };

  const renderGroup = (title, groupName, list, state) => (
    <Grid container sx={{ width: '100%', mt: 2 }}>
      <Grid item xs={4} sx={{ pr: 3 }}>
        <p className={`${styles.dataTitleV2} ${styles.wrappedLabel}`}>{title} *</p>
      </Grid>
      <Grid item xs={8} sx={{ mt: 0.5 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map(label => {
            const isDisabled = groupName === 'options' ? disabledOptions.has(label) : (groupName === 'opportunities' ? disabledOpportunities.has(label) : false);
            const onClick = isDisabled ? undefined : () => toggleItem(groupName, label);
            return (
              <div
                key={`${groupName}-${label}`}
                style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: isDisabled ? 'default' : 'pointer', opacity: isDisabled ? 0.55 : 1 }}
                onClick={onClick}
                title={isDisabled ? 'Temporarily unavailable' : undefined}
              >
                <TickBox type={Boolean(state[label])} tickClick={onClick} usable={!isDisabled} />
                <div style={{ fontFamily: 'PT Sans', fontSize: 13, color: isDisabled ? '#9e9e9e' : undefined }}>{label}</div>
              </div>
            );
          })}
        </div>
      </Grid>
    </Grid>
  );

  const handleSubmit = async () => {
    if (!(String(jobNumber).trim())) {
      enqueueSnackbar('Please enter Job Number', { variant: 'warning' });
      return;
    }

    const payload = {
      jobNumber: String(jobNumber).trim(),
      revision: String(revision).trim(),
      risks: Object.entries(risks).filter(([, v]) => v).map(([k]) => k),
      options: Object.entries(options).filter(([, v]) => v).map(([k]) => k),
      opportunities: Object.entries(opportunities).filter(([, v]) => v).map(([k]) => k),
    };

    try {
      await dispatch(submitRooAndDownloadDocx(payload));
      enqueueSnackbar('Document generated.', { variant: 'success' });
      if (typeof onSubmit === 'function') onSubmit(payload);
      if (typeof onClose === 'function') onClose();
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || 'Failed to generate document';
      enqueueSnackbar(msg, { variant: 'error' });
    }
  };

  return (
    <div className={styles.backGround} ref={backdropRef}>
      <div
        className={styles.form}
        ref={formRef}
        style={{
          width: 'min(600px, 90vw)',
          maxHeight: '80vh',
          overflowY: 'auto',
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          paddingTop: 12,
          paddingBottom: 8,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.momentTitle}>MOMENT</div>
        <div className={styles.sectionTitle}>Risks, Options, & Opportunities (ROO)</div>

        <Grid container sx={{ width: '100%' }}>
          <Grid item xs={4} sx={{ pr: 3 }}>
            <p className={`${styles.dataTitleV2} ${styles.wrappedLabel}`}>1. Job Number? *</p>
          </Grid>
          <Grid item xs={8} sx={{ mt: 1 }}>
            <Textfeild
              initialData={jobNumber}
              onValueChange={(v) => setJobNumber(v)}
              placeholder={'Enter your answer'}
            />
          </Grid>
        </Grid>

        {renderGroup('2. Please select Risks which apply to this job:', 'risks', risksList, risks)}
        {renderGroup('3. Please select Options which apply to this job:', 'options', optionsList, options)}
        {renderGroup('4. Please select Opportunities which apply to this job:', 'opportunities', opportunitiesList, opportunities)}

        <Grid container sx={{ width: '100%', mt: 2 }}>
          <Grid item xs={4} sx={{ pr: 3 }}>
            <p className={`${styles.dataTitleV2} ${styles.wrappedLabel}`}>5. Revision? (Leave blank if Rev A)</p>
          </Grid>
          <Grid item xs={8} sx={{ mt: 1 }}>
            <Textfeild
              initialData={revision}
              onValueChange={(v) => setRevision(v)}
              placeholder={'e.g. B'}
            />
          </Grid>
        </Grid>

        <div className={styles.updateAndCancel} style={{ marginTop: 16 }}>
          <div className={styles.updateButton} onClick={rooState.isSubmitting ? undefined : handleSubmit}>
            <div className={buttonStyles.YesButtonComponentStyling}>{rooState.isSubmitting ? 'WORKING…' : 'SUBMIT'}</div>
          </div>
          <div className={styles.cancelButton} onClick={onClose}>
            <div className={buttonStyles.NoButtonComponentStyling}>CANCEL</div>
          </div>
        </div>

        <img className={styles.blueLogo} src={BlueLogo} alt="BlueLogo" style={{ alignSelf: 'center' }} />
      </div>
    </div>
  );
}

export default RisksOptionsForm;


